import { Box } from "@mui/joy";
import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";

import { ProjectProvider, TaskProvider } from "../provider";
import { AnimationWrapper, Header, AppNavigation, ImpersonationBanner } from "../components";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import { OfflineDashboard } from "../components/Offline/OfflineDashboard";
import { SyncManager } from "../components/Offline/SyncManager";

export default function PostAuthentication() {
    const [collapsed, setCollapsed] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const isOnline = useNetworkStatus();
    const location = useLocation();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 600);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const isQuickCreateRoute = location.pathname.includes("/project/new-quick");
    const isOpeningOfflineProject =
        /[?&]open=offline-/.test(location.search) ||
        location.pathname.includes("/offline");

    // True offline shell, or viewing a locally-saved project before sync
    const useOfflineShell =
        (!isOnline && !isQuickCreateRoute) || isOpeningOfflineProject;

    if (useOfflineShell) {
        return (
            <Box
                sx={{
                    height: "100dvh",
                    overflow: "auto",
                    bgcolor: "background.body",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <OfflineDashboard />
                {isOnline ? <SyncManager /> : null}
            </Box>
        );
    }

    if (!isOnline && isQuickCreateRoute) {
        return (
            <Box
                sx={{
                    height: "100dvh",
                    overflow: "auto",
                    bgcolor: "background.body",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <ProjectProvider>
                    <TaskProvider>
                        <AnimationWrapper>
                            <Outlet />
                        </AnimationWrapper>
                    </TaskProvider>
                </ProjectProvider>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: "100dvh",
                display: "flex",
                overflow: "hidden",
                position: "relative",
            }}
        >
            <AppNavigation
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
            />

            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "100dvh",
                    minWidth: 0,
                }}
            >
                <Header
                    onToggleCollapse={
                        isMobile
                            ? () => setIsMenuOpen(true)
                            : () => setCollapsed(!collapsed)
                    }
                    isCollapsed={collapsed}
                />
                <ImpersonationBanner />
                <Box
                    sx={{
                        flex: 1,
                        overflow: "auto",
                        minHeight: 0,
                        minWidth: 0,
                        display: "flex",
                        position: "relative",
                        pb: isMobile ? "64px" : 0,
                    }}
                >
                    <ProjectProvider>
                        <TaskProvider>
                            <AnimationWrapper>
                                <Outlet />
                            </AnimationWrapper>
                        </TaskProvider>
                    </ProjectProvider>
                </Box>
            </Box>
            <SyncManager />
        </Box>
    );
}
